'use client';
import type { ReactElement } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';

import { AddressSearch } from '@/components/shared/address-search';
import type { StructuredAddress } from '@/components/shared/address-search';
import type { InventoryItem } from '@/lib/services/inventory-items/types';

interface ItemInfoAddressSearchProps {
  /** React Hook Form instance for the inventory item. */
  form: UseFormReturn<Partial<InventoryItem>>;
  label?: string;
  required?: boolean;
}

/**
 * RHF bridge for the shared `<AddressSearch />`.
 *
 * The structured address is persisted as discrete `itemInfo.*` fields
 * (`streetAddress`, `suburb`, `state`, `postcode`, `country`) — the same shape
 * the backend reads for property form generation / ABA flows and for the
 * mortgage auto-link-by-address matcher. This adapter watches those fields,
 * feeds them to the controlled search component, and writes selections /
 * manual edits back through `setValue` (mode `onChange`, so validation and the
 * Save button stay in sync).
 *
 * `formattedAddress` from the provider is intentionally not persisted — it is a
 * UI-only convenience string and is not part of the stored `itemInfo` shape.
 */
export function ItemInfoAddressSearch({
  form,
  label,
  required
}: ItemInfoAddressSearchProps): ReactElement {
  const [streetAddress, suburb, state, postcode, country] = useWatch({
    control: form.control,
    name: [
      'itemInfo.streetAddress',
      'itemInfo.suburb',
      'itemInfo.state',
      'itemInfo.postcode',
      'itemInfo.country'
    ]
  }) as [string?, string?, string?, string?, string?];

  const value: StructuredAddress = {
    streetAddress: streetAddress ?? '',
    suburb: suburb ?? '',
    state: state ?? '',
    postcode: postcode ?? '',
    country: country ?? ''
  };

  const handleChange = (next: StructuredAddress): void => {
    const options = { shouldDirty: true, shouldValidate: true } as const;
    form.setValue('itemInfo.streetAddress', next.streetAddress, options);
    form.setValue('itemInfo.suburb', next.suburb, options);
    form.setValue('itemInfo.state', next.state, options);
    form.setValue('itemInfo.postcode', next.postcode, options);
    form.setValue('itemInfo.country', next.country, options);
  };

  // Collapse the per-field validation errors into the component's single error
  // slot (first one wins), so an invalid manual entry still surfaces feedback.
  const itemInfoErrors = form.formState.errors.itemInfo as unknown as
    | Record<string, { message?: string } | undefined>
    | undefined;
  const error =
    itemInfoErrors?.streetAddress?.message ??
    itemInfoErrors?.suburb?.message ??
    itemInfoErrors?.state?.message ??
    itemInfoErrors?.postcode?.message ??
    itemInfoErrors?.country?.message;

  return (
    <AddressSearch
      value={value}
      onChange={handleChange}
      label={label}
      required={required}
      error={error}
    />
  );
}
